import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import CampaignForm from './CampaignForm'
import LineItemForm from './LineItemForm'
import AdDv360CreateForm from '../../AdForms/AdDv360Create/AdDv360CreateForm'

import useContinuousForm from '../../../../hooks/formHooks/useContinuousForm'
import useRedirectFormDrawer from '../../../../features/components/FormDrawerWrapper/useRedirectFormDrawer'

import { clearAds } from '../../../../modules/actions/ads'
import { dv360RightUploadAdDataSelector } from '../../../../modules/selectors/rights'

import { ROUTE_PARAMS, ROUTES } from '../../../../constants/routes'
import { MODAL_TYPE } from '../../../../constants/other'
import { LINE_ITEM_TYPE } from './LineItemForm/fields'

export const CAMPAIGN_FORM = 'campaign_form'
export const LINE_ITEM_FORM = 'line_item_form'
export const AD_FORM = 'ad_form'

const UploadDV360AdCreativeForm = ({ setRedirectionParams, handleAdsExceptions }) => {
  const dispatch = useDispatch()

  const { campaign, lineItem } = useSelector(dv360RightUploadAdDataSelector)
  // on get single lineItem endpoint we have 'line_item_type' field, but on the list endpoint we have 'type' field
  const lineItemType = lineItem[LINE_ITEM_TYPE] || lineItem['type']

  const { activatedForm, setActivatedForm } = useContinuousForm({
    initialForm: CAMPAIGN_FORM
  })

  const handleSuccessRedirect = useRedirectFormDrawer(
    `${ROUTES.rightsSummary}?message=${MODAL_TYPE.rightDV360AdUploaded}`
  )

  const onSuccessAdFormSubmit = useCallback(() => {
    setRedirectionParams({
      [ROUTE_PARAMS.campaignId]: campaign.id,
      [ROUTE_PARAMS.lineItemId]: lineItem.id
    })

    dispatch(clearAds())
    handleSuccessRedirect()
  }, [dispatch, campaign.id, lineItem.id, handleSuccessRedirect, setRedirectionParams])

  const formProgressItems = useMemo(
    () => [
      {
        label: 'Campaign',
        isActive: activatedForm === CAMPAIGN_FORM,
        isSuccess: activatedForm === LINE_ITEM_FORM || activatedForm === AD_FORM
      },
      {
        label: 'Line item',
        isActive: activatedForm === LINE_ITEM_FORM,
        isSuccess: activatedForm === AD_FORM
      },
      {
        label: 'Creative assets',
        isActive: activatedForm === AD_FORM,
        isSuccess: false
      }
    ],
    [activatedForm]
  )

  const getActivatedForm = useCallback(() => {
    switch (activatedForm) {
      case AD_FORM:
        return (
          <AdDv360CreateForm
            handleAdsExceptions={handleAdsExceptions}
            formProgressItems={formProgressItems}
            onSuccessSubmit={onSuccessAdFormSubmit}
            lineItemType={lineItemType}
          />
        )
      case LINE_ITEM_FORM:
        return (
          <LineItemForm
            formProgressItems={formProgressItems}
            onSuccessSubmit={() => {
              setActivatedForm(AD_FORM)
            }}
          />
        )
      case CAMPAIGN_FORM:
      default:
        return (
          <CampaignForm
            formProgressItems={formProgressItems}
            onSuccessSubmit={() => {
              setActivatedForm(LINE_ITEM_FORM)
            }}
          />
        )
    }
  }, [activatedForm, formProgressItems, onSuccessAdFormSubmit, setActivatedForm, handleAdsExceptions, lineItemType])

  return <>{getActivatedForm()}</>
}

UploadDV360AdCreativeForm.propTypes = {
  setRedirectionParams: PropTypes.func.isRequired
}

export default UploadDV360AdCreativeForm
